/**
 * Validation limits, operational thresholds, and other constants
 * referenced by schemas, UI, and business logic.
 */

// ---------------------------------------------------------------------------
// Field length limits
// ---------------------------------------------------------------------------

export const FIELD_LIMITS = {
  /** Names of suppliers, clients, departments, etc. */
  NAME_MAX: 200,
  /** Free-text address fields */
  ADDRESS_MAX: 500,
  /** Notes / remarks / special instructions */
  NOTES_MAX: 2000,
  /** Phone numbers (free text — light validation, allow international formats) */
  PHONE_MAX: 50,
  /** Username and display name */
  USERNAME_MAX: 60,
  DISPLAY_NAME_MAX: 200,
  EMAIL_MAX: 320,
  /** Project title and project number */
  PROJECT_TITLE_MAX: 300,
  PROJECT_NUMBER_MAX: 60,
  /** Project item references */
  ITEM_NUMBER_MAX: 100,
  UOM_MAX: 40,
  /** Project-level contract / commercial metadata */
  ORIGIN_MAX: 200,
  INDENT_NUMBER_MAX: 100,
  MAKER_NAME_BRAND_MAX: 300,
  CURRENCY_CODE_MAX: 20,
  BANK_ACCOUNT_MAX: 100,
  SWIFT_CODE_MAX: 50,
  REFERENCE_NUMBER_MAX: 100,
  /** Incoterm / terms-of-delivery master code */
  INCOTERM_CODE_MAX: 20,
  INCOTERM_LABEL_MAX: 120,
  /** Rejection comment on a parts submission */
  REJECTION_COMMENT_MAX: 2000,
  /** Cancellation reason on an order batch */
  CANCELLATION_REASON_MAX: 1000,
  /** Inspection event notes */
  INSPECTION_NOTES_MAX: 2000,
  /** BG notes */
  BG_NOTES_MAX: 2000,
} as const;

// ---------------------------------------------------------------------------
// Numeric limits
// ---------------------------------------------------------------------------

export const NUMERIC_LIMITS = {
  /** Sr# on parts — positive integer, reasonable upper bound */
  SR_NUMBER_MAX: 9999,
  /** Quantities — positive integer */
  QUANTITY_MAX: 9_999_999,
  /** Currency amounts (paisa) — bigint conceptually; we store as number up to safe integer */
  AMOUNT_MAX_PAISA: Number.MAX_SAFE_INTEGER,
  /** Warranty period (months) */
  WARRANTY_MONTHS_MAX: 240,
  /** Delivery duration (months) from contract sign date to effective delivery target */
  DELIVERY_DURATION_MONTHS_MAX: 240,
  /** Grace period (days) */
  GRACE_DAYS_MAX: 365,
  /** Percentages stored as basis points: 100.00% = 10000 */
  PERCENT_BPS_MAX: 10_000,
  /** Planned BG submission deadline, in days from contract signing */
  BG_SUBMISSION_DAYS_MAX: 365,
  /** Planned BG validity duration, in months */
  BG_VALIDITY_MONTHS_MAX: 240,
} as const;

// ---------------------------------------------------------------------------
// Auth & session
// ---------------------------------------------------------------------------

export const AUTH = {
  PASSWORD_MIN_LENGTH: 12,
  PASSWORD_MAX_LENGTH: 256,
  LOGIN_RATE_LIMIT_WINDOW_MINUTES: 15,
  LOGIN_RATE_LIMIT_ATTEMPTS: 5,
  SESSION_IDLE_TIMEOUT_HOURS: 12,
  SESSION_ABSOLUTE_TIMEOUT_DAYS: 30,
} as const;

// ---------------------------------------------------------------------------
// Operational thresholds
// ---------------------------------------------------------------------------

export const OPERATIONAL = {
  /** Days lead before computed Warranty End at which the BG/Warranty Watch alert fires */
  BG_WARRANTY_WATCH_LEAD_DAYS: 30,
  /** Days idle in a stage — currently NOT used as overdue threshold (overdue requires
   *  explicit expected dates; days-in-stage is informational only). Kept here for future use. */
  DAYS_IN_STAGE_HIGHLIGHT: 7,
  /** Polling interval for the notification feed (milliseconds) */
  NOTIFICATION_POLL_INTERVAL_MS: 30_000,
} as const;

// ---------------------------------------------------------------------------
// Seed values
// ---------------------------------------------------------------------------

export const SEED = {
  DEFAULT_CLIENT_NAME: 'Airforce',
} as const;
